/**
 * What can be verified at source, account by account.
 *
 * Joins the access tables (`/api/raketech/access-status`) to the claim rows on
 * the deal code, and classifies each row by why its figure cannot be tested:
 * no Adwin login, Raketech's own comment that the figure came from invoices or
 * from Finance rather than the account, a closed account, Raketech's PA = NO
 * classification, or no adwstats account. Used by the Unverifiable and No
 * access tabs of Final Report v4.
 */

import { canonCode, codeTokens, normBrand } from '@shared/raketech/invoiceAttribution';
import type { AccessStatusResponse } from '@shared/raketech/accessStatus.types';
import type { ClaimRow } from '../types/raketechFinalReportV4.types';

export type Tone = 'red' | 'amber' | 'sky' | 'emerald' | 'zinc';

/** Raketech saying the figure did not come from the account. */
const FROM_INVOICES_RE = /revenue reported from operator invoices|no data (rs )?in the account|as per invoices|provided by finance|reported by finance|as reported by finance|unable to log in|bill history/i;
const CLOSED_RE = /account closed/i;

export type VerifyReason = 'no_login' | 'shared' | 'from_invoices' | 'closed' | 'non_pa' | 'not_in_adwstats';

export const VERIFY_LABELS: Record<VerifyReason, { short: string; long: string; tone: Tone }> = {
  no_login: {
    short: 'No Adwin login',
    long: 'Adwin holds no login for the account; the traffic ran through an account under Raketech\'s own username.',
    tone: 'red',
  },
  shared: {
    short: 'Raketech says shared',
    long: 'When Adwin asked for the login, Raketech said the account is shared with other publishers. Clause 3.3.1 required Partner Accounts to be exclusive to Adwin from 1 March 2023; Raketech admitted on 2 July 2025 that some were shared "by mistake".',
    tone: 'amber',
  },
  from_invoices: {
    short: 'Figure not from the account',
    long: 'Raketech\'s own comment says the figure was taken from operator invoices, from Finance, or that Raketech itself could not log in.',
    tone: 'amber',
  },
  closed: {
    short: 'Closed, migrated or data-only',
    long: 'Raketech marks the account closed, migrated or data-only in its July 2026 report, or its June 2025 comment says the account is closed; the backend can no longer be checked.',
    tone: 'zinc',
  },
  non_pa: {
    short: 'Raketech: not a Partner Account',
    long: 'Raketech classifies the account PA = NO, which is its stated basis for not granting access. Adwin does not accept the classification.',
    tone: 'sky',
  },
  not_in_adwstats: {
    short: 'No adwstats account',
    long: 'No operator-backend or API connection exists for the account in adwstats, so the only figures are Raketech\'s.',
    tone: 'zinc',
  },
};

export type MatchedOn = 'username' | 'code' | 'brand';

export interface AccessInfo {
  /** Deal codes from the no-login list that map to this account. */
  noLoginOffers: string[];
  /** Raketech's network usernames those sit under. */
  noLoginUsernames: string[];
  /** What tied each no-login row to this account. Username and code together
   *  is certain; code alone with a sibling carrying the same code is not. */
  matchedOn: MatchedOn[];
  /** Set when a no-login row could equally have gone to another account. */
  ambiguous?: string[];
  verifiedStatus?: string;
  verifiedComment?: string;
  backend?: string;
  /** Adwin's own verification note says the account could not be reached. */
  notAccessible: boolean;
  /** Raketech told Adwin the account is shared with other publishers. */
  shared: boolean;
}

/** Adwin's verification notes that mean the account could not be opened. */
const NOT_ACCESSIBLE_RE = /not accessible|no access|cannot access|can't access|cant access|cant verify|cant check|cant login|can't login|login not available|password needed|logged out/i;
const SHARED_RE = /shared account|shared with|is shared/i;

/**
 * Joins the access tables to the claim rows.
 *
 * The no-login list names a deal code and the Raketech username the account
 * sits under. Neither alone is enough: one partner can run several accounts
 * under one code (PariMatch 12435 and 13826 both carry RT_PM_PM_A08_BR), and
 * one network username can hold several brands. So every row is scored
 * against every account on username, code and brand, and the account that
 * agrees on most takes it; where two agree equally the row is placed on the
 * first and both are named so the judgement is visible.
 *
 * `usernamesById` supplies the usernames Raketech's June file records per
 * account, which the claim rows carry only as a first name.
 */
export function accessByAccount(
  rows: ClaimRow[],
  access: AccessStatusResponse | null,
  usernamesById?: Map<string, string[]>,
): Map<string, AccessInfo> {
  const out = new Map<string, AccessInfo>();
  const get = (id: string) => {
    let a = out.get(id);
    if (!a) {
      a = { noLoginOffers: [], noLoginUsernames: [], matchedOn: [], notAccessible: false, shared: false };
      out.set(id, a);
    }
    return a;
  };
  if (!access) return out;

  const byCode = new Map<string, Set<string>>();
  const byUser = new Map<string, Set<string>>();
  const brandsOf = new Map<string, Set<string>>();
  const addCode = (code: string, id: string) => {
    for (const k of [canonCode(code), ...codeTokens(code)].filter(Boolean)) {
      if (!byCode.has(k)) byCode.set(k, new Set());
      byCode.get(k)!.add(id);
    }
  };
  const addUser = (user: string, id: string) => {
    const u = String(user || '').trim().toLowerCase();
    if (!u) return;
    if (!byUser.has(u)) byUser.set(u, new Set());
    byUser.get(u)!.add(id);
  };
  for (const r of rows) {
    if (!r.accountId) continue;
    for (const o of r.offers) addCode(o.offer, r.accountId);
    for (const e of r.invoiceEntries) addCode(String(e.label).split('—')[0], r.accountId);
    addUser(r.username, r.accountId);
    for (const u of usernamesById?.get(r.accountId) || []) addUser(u, r.accountId);
    brandsOf.set(r.accountId, new Set(r.brands.map(normBrand).filter(Boolean)));
  }

  const resolve = (offer: string, username: string, brand: string): { id: string; on: MatchedOn[]; also: string[] } | undefined => {
    const codeIds = new Set<string>();
    for (const k of [canonCode(offer), ...codeTokens(offer)].filter(Boolean)) {
      for (const id of byCode.get(k) || []) codeIds.add(id);
    }
    const userIds = byUser.get(String(username || '').trim().toLowerCase()) || new Set<string>();
    const nb = normBrand(brand);
    const scored = new Map<string, { score: number; on: MatchedOn[] }>();
    for (const id of new Set([...codeIds, ...userIds])) {
      const on: MatchedOn[] = [];
      let score = 0;
      if (userIds.has(id)) { on.push('username'); score += 2; }
      if (codeIds.has(id)) { on.push('code'); score += 2; }
      if (nb && brandsOf.get(id)?.has(nb)) { on.push('brand'); score += 1; }
      scored.set(id, { score, on });
    }
    if (scored.size === 0) return undefined;
    const best = Math.max(...[...scored.values()].map((s) => s.score));
    // A username alone can name a brand it does not carry; require at least
    // the code or the brand to agree with it.
    const top = [...scored.entries()].filter(([, s]) => s.score === best && (s.on.includes('code') || s.on.includes('brand')));
    if (top.length === 0) return undefined;
    return { id: top[0][0], on: top[0][1].on, also: top.slice(1).map(([id]) => id) };
  };

  for (const n of access.noLogin) {
    const r = resolve(n.offer, n.username, n.brand);
    if (!r) continue;
    const a = get(r.id);
    if (!a.noLoginOffers.includes(n.offer)) a.noLoginOffers.push(n.offer);
    if (n.username && !a.noLoginUsernames.includes(n.username)) a.noLoginUsernames.push(n.username);
    for (const m of r.on) if (!a.matchedOn.includes(m)) a.matchedOn.push(m);
    if (r.also.length > 0) a.ambiguous = [...new Set([...(a.ambiguous || []), ...r.also])];
  }
  for (const v of access.verified) {
    const id = v.raketechId || resolve(v.offer, v.username, '')?.id;
    if (!id) continue;
    const a = get(id);
    if (v.status && !a.verifiedStatus) a.verifiedStatus = v.status;
    if (v.comment && v.comment !== '—' && !a.verifiedComment) a.verifiedComment = v.comment;
    if (v.backend && !a.backend) a.backend = v.backend;
    if (NOT_ACCESSIBLE_RE.test(v.comment || '')) a.notAccessible = true;
    if (SHARED_RE.test(v.comment || '')) a.shared = true;
  }
  return out;
}

/** True when Adwin cannot open the account: on the no-login list, or Adwin's
 *  own note says it could not be reached or was declared shared. */
export const noAccess = (info: AccessInfo | undefined): boolean =>
  !!info && (info.noLoginOffers.length > 0 || info.notAccessible || info.shared);

export function verifyReasons(row: ClaimRow, info: AccessInfo | undefined): VerifyReason[] {
  const reasons: VerifyReason[] = [];
  if (info && (info.noLoginOffers.length > 0 || info.notAccessible)) reasons.push('no_login');
  if (info?.shared) reasons.push('shared');
  const julyComments = row.offers.map((o) => o.comment || '');
  if (julyComments.some((c) => FROM_INVOICES_RE.test(c)) || row.reference.juneComments.some((c) => FROM_INVOICES_RE.test(c))) {
    reasons.push('from_invoices');
  }
  const closed = (row.offers.length > 0 && row.offers.every((o) => o.status && o.status !== 'ACTIVE'))
    || row.reference.juneComments.some((c) => CLOSED_RE.test(c))
    || /closed|migrated/i.test(info?.verifiedStatus || '');
  if (closed) reasons.push('closed');
  if (row.offers.length > 0 && row.offers.every((o) => !o.pa)) reasons.push('non_pa');
  if (row.missingFromAdwstats) reasons.push('not_in_adwstats');
  return reasons;
}

/** Raketech's own words on the account, deduplicated, for display. */
export function raketechComments(row: ClaimRow): string[] {
  const out = new Set<string>();
  for (const o of row.offers) if (o.comment) out.add(o.comment.trim());
  for (const c of row.reference.juneComments) if (c) out.add(c.trim());
  return [...out];
}

export const paOf = (row: ClaimRow): 'yes' | 'no' | 'mixed' | 'unknown' => {
  if (row.offers.length === 0) return 'unknown';
  const yes = row.offers.filter((o) => o.pa).length;
  return yes === row.offers.length ? 'yes' : yes === 0 ? 'no' : 'mixed';
};

export const statusOf = (row: ClaimRow): string =>
  [...new Set(row.offers.map((o) => o.status).filter(Boolean))].join(' / ');

/**
 * Whether Raketech's figure for the account, as it stands now, equals what
 * Adwin invoiced to the cent.
 *
 * "As it stands now" is the July 2026 report where it carries the account and
 * the June 2025 credited column otherwise — never the Reported column, which
 * equals the invoice by construction and would make every write-down look like
 * a match. Each period is compared on its own footing: the reconciled period
 * against the invoicing for it, the later months against theirs; after April
 * 2025 the report is gross and Adwin invoiced 95%, so either basis counts.
 *
 * On an account Adwin can open, Raketech's figure comes from the operator and
 * differs from the invoice by the ordinary corrections; a figure that equals
 * the invoice to the cent is one that was taken from the invoice.
 */
type FigureRow = Pick<ClaimRow, 'candidates' | 'offers' | 'earnedTail' | 'invoicedRecon' | 'invoicedPostRecon' | 'invoicedTotal'>;

/** Raketech's figure for the account as it stands now, whole period: the July
 *  2026 report where it carries the account, else the June 2025 credited column
 *  (the reconciled period), plus its monthly reports from December 2024 on. */
export function theirCurrentFigure(row: FigureRow): number {
  const c = (s: string) => row.candidates.find((x) => x.source === s)?.amount || 0;
  const recon = row.offers.length > 0 ? c('raketech_restated') : c('june_recon');
  return recon + (row.earnedTail || 0);
}

export function matchesToTheCent(row: FigureRow): boolean {
  const c = (s: string) => row.candidates.find((x) => x.source === s)?.amount || 0;
  const currentRecon = row.offers.length > 0 ? c('raketech_restated') : c('june_recon');
  const tail = row.earnedTail || 0;
  const near = (a: number, b: number) => Math.abs(a - b) < 1;
  const reconOk = (currentRecon <= 0 && row.invoicedRecon <= 0) || near(currentRecon, row.invoicedRecon);
  const tailOk = (tail <= 0 && row.invoicedPostRecon <= 0)
    || near(tail, row.invoicedPostRecon)
    || near(tail * 0.95, row.invoicedPostRecon);
  return row.invoicedTotal > 0 && reconOk && tailOk;
}
